import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toast } from 'antd-mobile';
import { useI18n } from '../../../hooks/useI18n';
import { useDeviceDetect } from '../../../hooks/useDeviceDetect';
import { getTrainingScenes, type TrainingSceneInfo, TrainingStatus } from '@/lib/api/ai-training/scene';
import { getUserInfo } from '@/lib/api/user';
import { TimeUtils, TIME_FORMATS } from '@/utils/timeUtils';
import logo2 from '@/assets/images/ai/logo2.png';
import cover2 from '@/assets/images/ai/cover2.png';
import icon1 from '@/assets/images/ai/icon1.png';
import list2 from '@/assets/images/ai/list2.png';
import TrainingGuider from '@/components/TrainingGuider';

// 使用API中定义的接口
type TrainingItem = TrainingSceneInfo;

interface FilterTab {
  label: string;
  value: string;
}

const AiTrainingList: React.FC = () => {
  const { t } = useI18n();
  const { isMobile } = useDeviceDetect();
  const [trainList, setTrainList] = useState<TrainingItem[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>(TrainingStatus.IN_PROGRESS);
  const [isTabSticky, setIsTabSticky] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [aloading, setAloading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [allLoaded, setAllLoaded] = useState<boolean>(false);
  const [noData, setNoData] = useState<boolean>(false);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [userInfoLoaded, setUserInfoLoaded] = useState<boolean>(false);
  
  const navigate = useNavigate()
  const filterBoxRef = useRef<HTMLDivElement>(null);
  
  const filterTabs: FilterTab[] = [
    { label: t('ai.training.status.notStarted'), value: TrainingStatus.NOT_STARTED },
    { label: t('ai.training.status.inProgress'), value: TrainingStatus.IN_PROGRESS },
    { label: t('ai.training.status.expired'), value: TrainingStatus.EXPIRED },
  ];

  // 获取用户信息，判断是否为管理员
  const checkUserAdmin = async () => {
    try {
      const response = await getUserInfo();
      if (response.success && response.data) {
        const adminStatus = response.data.is_admin ?? response.data.is_superuser ?? false;
        setIsAdmin(adminStatus);
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
      setIsAdmin(false);
    } finally {
      setUserInfoLoaded(true);
    }
  };

  // 获取空状态文案的方法
  const getEmptyStateText = () => {
    const deviceType = isMobile ? 'mobile' : 'desktop';
    const userType = isAdmin ? 'admin' : 'student';
    const isExpired = filterStatus === TrainingStatus.EXPIRED;
    
    // 已过期状态只显示主文案，不显示副文案
    if (isExpired) {
      return {
        mainText: t(`ai.training.emptyState.${deviceType}.${userType}.mainText`),
        subText: ''
      };
    }
    
    return {
      mainText: t(`ai.training.emptyState.${deviceType}.${userType}.mainText`),
      subText: t(`ai.training.emptyState.${deviceType}.${userType}.subText`)
    };
  };

  // 滚动监听
  const handleScroll = useCallback(() => {
    if (!filterBoxRef.current) return;
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const shouldStick = scrollTop > headerHeight;
    
    if (shouldStick !== isTabSticky) {
      setIsTabSticky(shouldStick);
    }
  }, [headerHeight, isTabSticky]);

  // 计算头部高度
  const calculateHeaderHeight = useCallback(() => {
    if (filterBoxRef.current) {
      const rect = filterBoxRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setHeaderHeight(rect.top + scrollTop - 2);
    }
  }, []);

  // 切换筛选状态
  const changeFilter = (status: string) => {
    setTrainList([]);
    setPage(1);
    setLoading(false);
    setAllLoaded(false);
    setNoData(false);
    setFilterStatus(status);
    // useEffect会监听filterStatus和page的变化自动调用getTrainList
  };

  // 加载更多
  const loadMore = () => {
    if (allLoaded || noData || loading) return;
    setPage(prev => prev + 1);
  };

  // 获取训练列表
  const getTrainList = async () => {
    if (page === 1) {
      setAloading(true);
    } else {
      setLoading(true);
    }
    
    try {
      const response = await getTrainingScenes({
        time_status: filterStatus as any,
        page,
        pageSize: 20
      });
      
      if (response.success && response.data) {
        const newList = response.data.list;
        
        if (page === 1) {
          setTrainList(newList);
          setAloading(false);
        } else {
          setTrainList(prev => [...prev, ...newList]);
        }
        
        // 检查是否还有更多数据
        setAllLoaded(newList.length < 20);
        setNoData(page === 1 && newList.length === 0);
      } else {
        throw new Error(response.errorMessage || '获取数据失败');
      }
    } catch (error) {
      console.error('获取训练列表失败:', error);
      Toast.show({
        content: t('ai.training.error.loadFailed'),
        icon: 'fail'
      });
      
      if (page === 1) {
        setNoData(true);
        setAloading(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // 跳转到详情页
  const toTrainDetail = (item: TrainingItem) => {
    navigate(`/ai-training/detail/${item.scenario_id}`);
  };

  useEffect(() => {
    getTrainList();
  }, [page, filterStatus]);
  
  useEffect(() => {
    // 获取用户信息
    checkUserAdmin();
    
    // 添加滚动监听
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // 延迟计算头部高度
    setTimeout(calculateHeaderHeight, 100);
    
    // 监听窗口大小变化
    const handleResize = () => {
      calculateHeaderHeight();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleScroll, calculateHeaderHeight]);

  return (
    <div 
      className="min-h-screen relative"
      style={{
        backgroundImage: `url(${list2})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* 引导组件 */}
      <TrainingGuider />

      {/* 响应式容器 */}
      <div className="w-full max-w-md mx-auto md:max-w-2xl lg:max-w-4xl xl:max-w-6xl px-5 py-5 md:px-8 md:py-8">
      {/* 头部区域 */}
      <motion.div 
        className="flex justify-between items-start mb-6 md:mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <motion.div 
          className="flex-shrink-0"
          style={{ 
            width: '84px', 
            height: '110px'
          }}
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <img 
            className="w-full h-full object-contain md:w-24 md:h-32 lg:w-28 lg:h-36" 
            src={logo2} 
            alt="Logo" 
          />
        </motion.div>
        <div 
          className="text-left flex-1" 
          style={{ 
            fontFamily: 'Alimama ShuHeiTi, Alimama ShuHeiTi',
            marginLeft: '14px',
            marginTop: '8px'
          }}
        >
          <motion.div 
            className="font-semibold text-gray-800"
            style={{
              fontSize: '24px',
              lineHeight: '40px',
              marginBottom: '12px'
            }}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span style={{ color: '#7c58cb' }} className="text-2xl md:text-3xl lg:text-4xl">AI</span>
            <span className="text-2xl md:text-3xl lg:text-4xl">{t('ai.training.title')}</span>
          </motion.div>
          <motion.div 
            className="text-gray-700 text-sm md:text-base lg:text-lg"
            style={{
              fontSize: '14px',
              lineHeight: '22px',
              color: '#303133'
            }}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {t('ai.training.subtitle')}
          </motion.div>
        </div>
      </motion.div>

      {/* 筛选tab */}
      <div
      data-tour="aitraining-list-tab"
        ref={filterBoxRef}
        className={`relative pb-3 z-50 bg-transparent transition-all duration-300 ${
          isTabSticky ? 'sticky-active' : ''
        }`}
        style={{ padding: '6px 0' }}
      >
        <motion.div
          className={`flex items-center justify-around bg-white rounded-full shadow-sm z-10 transition-all duration-300 max-w-sm mx-auto md:max-w-md ${
            isTabSticky
              ? 'fixed top-2 left-[10%] right-[10%]  z-50 -translate-y-0.5 pt-5 max-w-sm md:max-w-md'
              : ''
          }`}
          style={{
            padding: '8px 0',
            borderRadius: '32px',
            minHeight: '32px',
            boxShadow: '0 1px 4px 0 rgba(23, 12, 48, 0.04)'
          }}
          layout
        >
          {filterTabs.map((tab) => (
            <motion.div
              key={tab.value}
              className={`cursor-pointer relative rounded-full transition-all duration-200 ${
                filterStatus === tab.value ? 'text-purple-600 font-semibold' : 'text-gray-400'
              }`}
              style={{
                fontSize: '16px',
                padding: '0 12px 4px 12px'
              }}
              onClick={() => changeFilter(tab.value)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {tab.label}
              {filterStatus === tab.value && (
                <motion.div
                  className="absolute bottom-0 left-1/5 w-3/5 h-0.5 bg-purple-600 rounded-sm"
                  layoutId="activeTab"
                  initial={false}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
            </motion.div>
          ))}
        </motion.div>
        
        {/* 吸顶时的占位空间 */}
        {isTabSticky && (
          <div style={{ height: '44px', width: '100%' }} />
        )}
      </div>

      {/* 列表区域 */}
      <div 
        className={`overflow-visible h-auto relative ${
          isTabSticky ? 'mask-active -mt-2.5 pt-5' : ''
        }`}
      >
        {/* 吸顶时的渐变遮罩 */}
        {isTabSticky && (
          <div 
            className="absolute top-0 -left-5 -right-5 h-5 pointer-events-none z-10"
            style={{
              background: 'linear-gradient(180deg, rgba(248, 246, 255, 0.6) 0%, rgba(248, 246, 255, 0.3) 50%, transparent 100%)'
            }}
          />
        )}
        
        <AnimatePresence mode="wait">
          {trainList.length > 0 ? (
            <motion.div
              key="trainList"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {trainList.map((item, index) => (
                <motion.div
                  key={item.id}
                  className="text-left cursor-pointer border-2 border-white transition-transform duration-300 hover:-translate-y-1 flex flex-col h-full"
                  style={{
                    padding: '12px',
                    borderRadius: '16px',
                    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.31) 0%, rgba(220, 206, 255, 0.19) 100%)',
                    boxShadow: '0px 0px 11px 2px rgba(23, 12, 48, 0.08)'
                  }}
                  onClick={() => toTrainDetail(item)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  whileHover={{ 
                    scale: 1.02,
                    boxShadow: '0px 4px 20px 4px rgba(23, 12, 48, 0.12)'
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="w-full mb-3">
                    <motion.img
                      className="w-full rounded-lg object-cover"
                      style={{ aspectRatio: '16/9', height: 'auto' }}
                      src={item.scenario_cover}
                      alt={item.scenario_name}
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                    />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div 
                      className="font-medium text-gray-800 truncate mb-2 text-base md:text-lg"
                      style={{
                        fontSize: '16px',
                        lineHeight: '24px'
                      }}
                    >
                      {item.scenario_name}
                    </div>
                    <div 
                      className="font-normal text-gray-600 flex items-center mt-auto text-sm md:text-base"
                      style={{
                        fontSize: '14px',
                        lineHeight: '22px'
                      }}
                    >
                      <img 
                        style={{ 
                          width: '18px', 
                          height: '18px', 
                          marginRight: '6px' 
                        }} 
                        src={icon1} 
                        alt="" 
                      />
                      <span>
                        {item.effective_start_time && item.effective_end_time
                          ? `${TimeUtils.utcToLocal(item.effective_start_time, TIME_FORMATS.DATE)} - ${TimeUtils.utcToLocal(item.effective_end_time, TIME_FORMATS.DATE)}`
                          : t('ai.training.longTerm')}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : aloading ? (
            <motion.div 
              key="loading"
              className="flex flex-col justify-center items-center"
              style={{ minHeight: '150px' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="border-2 border-gray-300 border-t-purple-600 rounded-full"
                style={{
                  width: '20px',
                  height: '20px',
                  marginBottom: '6px'
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
              <div 
                className="text-gray-600"
                style={{ fontSize: '14px' }}
              >
                {t('common.loading')}
              </div>
            </motion.div>
          ) : (
            <motion.div 
              key="empty"
              className="flex flex-col items-center justify-center"
              style={{ marginTop: '120px' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {userInfoLoaded && (
                <>
                  <div 
                    className="font-normal text-gray-600"
                    style={{
                      fontSize: '14px',
                      lineHeight: '24px',
                      marginBottom: '8px'
                    }}
                  >
                    {getEmptyStateText().mainText}
                  </div>
                  {getEmptyStateText().subText && (
                    <div
                      className="font-normal text-gray-500"
                      style={{
                        fontSize: '12px',
                        lineHeight: '22px',
                        marginTop: '4px',
                        textAlign: 'center',
                        padding: '0 20px'
                      }}
                    >
                      {getEmptyStateText().subText}
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </div>
    </div>
  );
};

export default AiTrainingList;